/**
 * PanelDomainErrorMessage.tsx
 *
 * Per-panel error chip rendered in the center of a panel's content area when
 * the panel's domain has a sync error. Replaces chart/table content; panel title
 * remains visible.
 *
 * Slice E: category-aware via `errorEnvelope` prop. Switches chip text, color,
 * and CTA based on `SyncErrorCode`. See brand guide §34.8.
 *
 * Panel chip reference (mockup 2026-05-05-per-panel-error-v1.html):
 *   RATE_LIMIT      → "Retrying in {N}s"   warning/amber    dimmed
 *   AUTH_EXPIRED    → "Reconnect required"  danger/red       dimmed + Reconnect → CTA
 *   SCHEMA_DRIFT    → "Data unavailable"    danger/red       dimmed
 *   TRANSIENT_5XX   → "Couldn't load — retry above"  warning/amber  dimmed
 *   NETWORK         → "Couldn't load — retry above"  warning/amber  dimmed
 *   PLATFORM        → "Couldn't load — retry above"  warning/amber  dimmed
 *   BUSINESS_EMPTY  → "No data for this scope"  muted/info  NOT dimmed
 *
 * Dimming (opacity-60 border-destructive/25 or opacity-70 border-warning/25) is
 * applied by the PanelRenderer caller, not here. BUSINESS_EMPTY exception: caller
 * must NOT dim (sync succeeded; scope is empty — informational only).
 *
 * Accessibility (brand guide §34.10):
 *   - Chip container: role="alert" for error categories (assertive on page load).
 *   - BUSINESS_EMPTY: role="status" (not an error).
 *   - AUTH_EXPIRED CTA: aria-label="Reconnect CampMinder to reload this panel".
 *   - Chip container: aria-label describes the reason the panel is empty.
 *
 * Note: `panelMinHeight` lets callers keep the chip vertically centered in
 * panels of varying height types (KPI ≈ 96px, chart ≈ 200px, table ≈ 192px).
 */

import React from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, AlertCircle, WifiOff, Clock, Info, KeyRound } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { SyncErrorEnvelope } from '@/types/SyncError.types'

// ─── Category look-up tables ──────────────────────────────────────────────────

const CHIP_TEXT: Record<string, string> = {
  RATE_LIMIT: 'Retrying in {N}s',   // caller substitutes {N} via rateLimitSecondsLeft
  AUTH_EXPIRED: 'Reconnect required',
  // AUTH_INVALID — surfaced when CampMinder rejects the credential and
  // expireDate is null/future. User mental model: "verify the key" — chip
  // copy nudges toward the same Reconnect CTA but acknowledges the cause.
  // Slice F bugbash 2026-05-06.
  AUTH_INVALID: 'Credentials invalid',
  SCHEMA_DRIFT: 'Data unavailable',
  TRANSIENT_5XX: "Couldn't load — retry above",
  NETWORK: "Couldn't load — retry above",
  PLATFORM: "Couldn't load — retry above",
  BUSINESS_EMPTY: 'No data for this scope',
}

/** Chip text for a given code. Substitutes rate-limit countdown when provided. */
function getChipText(code: string | undefined, rateLimitSecondsLeft?: number): string {
  if (code === 'RATE_LIMIT') {
    return `Retrying in ${rateLimitSecondsLeft ?? '…'}s`
  }
  return CHIP_TEXT[code ?? ''] ?? "Couldn't load — retry above"
}

/** Class string for chip visual family per brand guide §34.8. */
function getChipClass(code: string | undefined): string {
  if (code === 'AUTH_EXPIRED' || code === 'AUTH_INVALID' || code === 'SCHEMA_DRIFT') {
    // danger/red
    return 'text-[hsl(0_63%_42%)] bg-destructive/5 border-destructive/20'
  }
  if (code === 'BUSINESS_EMPTY') {
    // muted/informational
    return 'text-muted-foreground bg-muted border-border'
  }
  // warning/amber (RATE_LIMIT, TRANSIENT_5XX, NETWORK, PLATFORM)
  return 'text-[hsl(34_100%_30%)] bg-warning-foreground border-warning/30'
}

/** Icon for chip — per brand guide §34.2 + §34.8. */
function getChipIcon(code: string | undefined): React.ReactNode {
  switch (code) {
    case 'AUTH_EXPIRED':
    case 'AUTH_INVALID':
      return <KeyRound size={12} aria-hidden="true" />
    case 'SCHEMA_DRIFT':
      return <AlertCircle size={12} aria-hidden="true" />
    case 'NETWORK':
      return <WifiOff size={12} aria-hidden="true" />
    case 'RATE_LIMIT':
      return <Clock size={12} aria-hidden="true" />
    case 'BUSINESS_EMPTY':
      return <Info size={12} aria-hidden="true" />
    default:
      // TRANSIENT_5XX, PLATFORM, unknown
      return <AlertTriangle size={12} aria-hidden="true" />
  }
}

/** ARIA label that fully describes why the panel is empty. */
function getAriaLabel(code: string | undefined, domainName: string): string {
  switch (code) {
    case 'RATE_LIMIT':
      return `${domainName} panel paused — rate limited, retrying automatically`
    case 'AUTH_EXPIRED':
      return `${domainName} panel unavailable — CampMinder credentials expired, reconnect required`
    case 'AUTH_INVALID':
      return `${domainName} panel unavailable — CampMinder credentials not working, verify or reconnect`
    case 'SCHEMA_DRIFT':
      return `${domainName} panel unavailable — unexpected data structure from CampMinder`
    case 'TRANSIENT_5XX':
      return `${domainName} panel unavailable — CampMinder server error, retry in banner above`
    case 'NETWORK':
      return `${domainName} panel unavailable — network error, retry in banner above`
    case 'PLATFORM':
      return `${domainName} panel unavailable — internal error, retry in banner above`
    case 'BUSINESS_EMPTY':
      return `${domainName} panel has no data for the selected scope`
    default:
      return `${domainName} panel unavailable — retry in banner above`
  }
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface PanelDomainErrorMessageProps {
  /** Display name of the domain (e.g. "Enrollment", "Bunks"). Used in ARIA labels. */
  domainName: string
  /**
   * Sync error envelope for category-aware chip (Slice E).
   * When absent, falls back to generic amber warning chip.
   */
  errorEnvelope?: SyncErrorEnvelope | null
  /**
   * Seconds remaining in rate-limit window.
   * Only relevant when errorEnvelope.code === 'RATE_LIMIT'.
   */
  rateLimitSecondsLeft?: number
  /**
   * Credential route for AUTH_EXPIRED CTA (e.g. "/credentials/abc123").
   * Falls back to "/credentials" when absent.
   */
  credentialHref?: string
  /**
   * Minimum height of the centring container. Matches the panel content area
   * so the chip is vertically centred regardless of panel type.
   * Defaults to 80px (covers KPI height at min).
   */
  panelMinHeight?: string | number
}

// ─── Component ────────────────────────────────────────────────────────────────

export const PanelDomainErrorMessage: React.FC<PanelDomainErrorMessageProps> = ({
  domainName,
  errorEnvelope,
  rateLimitSecondsLeft,
  credentialHref = '/credentials',
  panelMinHeight = 80,
}) => {
  const code = errorEnvelope?.code
  const isBusinessEmpty = code === 'BUSINESS_EMPTY'
  const isAuthExpired = code === 'AUTH_EXPIRED' || code === 'AUTH_INVALID'

  const chipText = getChipText(code, rateLimitSecondsLeft)
  const chipClass = getChipClass(code)
  const chipIcon = getChipIcon(code)
  const ariaLabel = getAriaLabel(code, domainName)

  const minHeightStyle: React.CSSProperties =
    typeof panelMinHeight === 'number'
      ? { minHeight: `${panelMinHeight}px` }
      : { minHeight: panelMinHeight }

  return (
    <div
      className="flex items-center justify-center w-full"
      style={minHeightStyle}
      role={isBusinessEmpty ? 'status' : 'alert'}
      aria-label={ariaLabel}
      data-testid="panel-domain-error-message"
    >
      {isAuthExpired ? (
        /* AUTH_EXPIRED: chip + navigation CTA side-by-side */
        <div className="flex flex-col items-center gap-1.5">
          <span
            className={cn(
              'inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border',
              chipClass,
            )}
          >
            {chipIcon}
            {chipText}
          </span>
          <Link
            to={credentialHref}
            className="text-[10.5px] font-semibold text-[hsl(0_63%_42%)] underline hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusRing focus-visible:rounded-sm"
            aria-label="Reconnect CampMinder to reload this panel"
          >
            Reconnect →
          </Link>
        </div>
      ) : (
        <span
          className={cn(
            'inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border',
            chipClass,
          )}
        >
          {chipIcon}
          {chipText}
        </span>
      )}
    </div>
  )
}
